import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export function AIKnowledgeDialog({
  open,
  onClose,
  projectId,
  canManage,
  initialMemory,
  onTask,
  onDocument,
  embedded = false,
  activeTab,
}: {
  embedded?: boolean;
  activeTab?: "memory" | "mcp" | "sources";
  open: boolean;
  onClose(): void;
  projectId: string;
  canManage: boolean;
  initialMemory?: string;
  onTask?(id: string): void;
  onDocument?(id: string): void;
}) {
  const fr = useI18n().language === "fr";
  const client = useQueryClient();
  const [tab, setTab] = useState<"memory" | "mcp" | "sources">("memory");
  const [content, setContent] = useState("");
  const [scope, setScope] = useState("USER");
  const [label, setLabel] = useState("");
  const [tokenWrite, setTokenWrite] = useState(false);
  const [tokenBulk, setTokenBulk] = useState(false);
  const [tokenDestructive, setTokenDestructive] = useState(false);
  const [newToken, setNewToken] = useState("");
  const [tokenHistory, setTokenHistory] = useState(false);
  const [replaceId, setReplaceId] = useState<string | null>(null);
  const memories = useQuery({
    queryKey: ["ai-memories", projectId],
    queryFn: () =>
      apiGet<{ memories: { id: string; scope: string; content: string }[] }>(
        projectId ? `/ai/projects/${projectId}/memories` : `/ai/assistant/memories`,
      ),
    enabled: open,
  });
  const graph = useQuery({
    queryKey: ["ai-graph", projectId],
    queryFn: () =>
      apiGet<{
        tasks: { id: string; title: string }[];
        coverage: { viewId: string; name: string; linkedTaskIds: string[] }[];
        staleSources: { sourceViewId: string; taskId: string }[];
        truncated: boolean;
      }>(`/ai/projects/${projectId}/graph`),
    enabled: open && tab === "sources",
  });
  const tokens = useQuery({
    queryKey: ["ai-tokens", projectId, tokenHistory],
    queryFn: () =>
      apiGet<{
        tokens: {
          id: string;
          label: string;
          revokedAt: string | null;
          expiresAt: string;
        }[];
      }>(`/ai/tokens?${new URLSearchParams({ ...(projectId ? { projectId } : {}), history: String(tokenHistory) })}`),
    enabled: open && tab === "mcp",
  });
  useEffect(() => {
    if (open && initialMemory) {
      setContent(initialMemory);
      setTab("memory");
    }
    if (!open) setNewToken("");
  }, [open, initialMemory]);
  useEffect(() => { setNewToken(""); setTokenHistory(false); setReplaceId(null); }, [projectId]);
  useEffect(() => {
    if (activeTab) setTab(activeTab);
  }, [activeTab]);
  const save = useMutation({
    mutationFn: () =>
      apiPost(projectId ? `/ai/projects/${projectId}/memories` : `/ai/assistant/memories`, {
        scope,
        type: scope === "USER" ? "PREFERENCE" : "CONVENTION",
        content,
      }),
    onSuccess: () => {
      setContent("");
      void client.invalidateQueries({ queryKey: ["ai-memories", projectId] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      apiDelete(projectId ? `/ai/projects/${projectId}/memories/${id}` : `/ai/assistant/memories/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["ai-memories", projectId] });
    },
  });
  const createToken = useMutation({
    mutationFn: () =>
      apiPost<{ token: string }>("/ai/tokens", {
        label,
        projectIds: [projectId],
        scopes: [
          "read",
          "propose",
          ...(tokenWrite ? ["write"] : []),
          ...(tokenWrite && tokenBulk ? ["bulk"] : []),
          ...(tokenWrite && tokenDestructive ? ["destructive"] : []),
        ],
        days: 30,
      }),
    onSuccess: (result) => {
      setNewToken(result.token);
      void client.invalidateQueries({ queryKey: ["ai-tokens"] });
    },
  });
  const replace = useMutation({
    mutationFn: (id: string) => apiPost<{ token: string }>(`/ai/tokens/${id}/replace`, {}),
    onSuccess: (result) => { setNewToken(result.token); setReplaceId(null); void client.invalidateQueries({ queryKey: ["ai-tokens"] }); },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => apiDelete(`/ai/tokens/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["ai-tokens"] });
    },
  });
  const error =
    save.error ||
    remove.error ||
    createToken.error ||
    revoke.error ||
    replace.error ||
    memories.error ||
    tokens.error;
  const contents = (
    <div className="space-y-4">
      {" "}
      {!embedded && (
        <div className="flex gap-2">
          <Button
            variant={tab === "memory" ? "default" : "outline"}
            size="sm"
            onClick={() => setTab("memory")}
          >
            {fr ? "Mémoire" : "Memory"}
          </Button>
          <Button
            variant={tab === "mcp" ? "default" : "outline"}
            size="sm"
            onClick={() => setTab("mcp")}
          >
            MCP
          </Button>
          <Button
            variant={tab === "sources" ? "default" : "outline"}
            size="sm"
            onClick={() => setTab("sources")}
          >
            {fr ? "Sources" : "Sources"}
          </Button>
        </div>
      )}
      {tab === "sources" ? (
        <div className="space-y-3 text-sm">
          <p className="text-foreground/70">
            {fr
              ? "Les liens indiquent l’origine des tâches. Ils ne prouvent pas que toutes les exigences sont couvertes."
              : "Links record task origins. They do not prove complete requirements coverage."}
          </p>
          {graph.data?.truncated && (
            <p>
              {fr
                ? "Résultats partiels : affinez l’analyse dans le chat."
                : "Partial results: narrow the analysis in chat."}
            </p>
          )}
          {graph.data?.coverage.map((source) => (
            <div
              key={source.viewId}
              className="rounded border border-white/10 p-3"
            >
              <button
                className="text-primary font-medium"
                onClick={() => {
                  onDocument?.(source.viewId);
                  onClose();
                }}
              >
                {source.name}
              </button>
              {source.linkedTaskIds.map((id) => (
                <button
                  key={id}
                  className="block text-left px-2 py-1.5 rounded-md hover:bg-white/5 transition-colors"
                  onClick={() => {
                    onTask?.(id);
                    onClose();
                  }}
                >
                  {graph.data?.tasks.find((task) => task.id === id)?.title ??
                    id}
                  {graph.data?.staleSources.some(
                    (link) =>
                      link.taskId === id && link.sourceViewId === source.viewId,
                  ) && (
                    <span className="ml-2 text-amber-500">
                      {fr ? "Source modifiée" : "Source changed"}
                    </span>
                  )}
                </button>
              ))}
              {!source.linkedTaskIds.length && (
                <p className="text-xs text-foreground/70">
                  {fr ? "Aucune tâche liée" : "No linked tasks"}
                </p>
              )}
            </div>
          ))}
          {graph.error && (
            <p role="alert" className="text-destructive">
              {graph.error.message}
            </p>
          )}
        </div>
      ) : tab === "memory" ? (
        <div className="space-y-3">
          <p className="text-sm text-foreground/70">
            {fr
              ? "Les souvenirs personnels restent dans vos chats privés. Les conventions du projet sont partagées et nécessitent un accord explicite."
              : "Personal memories stay in your private chats. Project conventions are shared and require explicit approval."}
          </p>
          {memories.data?.memories.map((memory) => (
            <div
              key={memory.id}
              className="rounded border border-white/10 p-3 text-sm"
            >
              <p className="text-xs text-foreground/70 mb-1">
                {memory.scope === "USER"
                  ? fr
                    ? "Personnel"
                    : "Personal"
                  : fr
                    ? "Projet"
                    : "Project"}
              </p>
              <p className="whitespace-pre-wrap">{memory.content}</p>
              {(memory.scope === "USER" || canManage) && (
                <button
                  className="text-xs text-destructive mt-2 rounded-md px-2 py-1 hover:bg-destructive/10 transition-colors"
                  onClick={() => remove.mutate(memory.id)}
                >
                  {fr ? "Supprimer" : "Delete"}
                </button>
              )}
            </div>
          ))}
          <label className="block text-sm">
            {fr ? "Enregistrer un souvenir validé" : "Save a reviewed memory"}
            <textarea
              className="w-full mt-2 rounded-lg border border-white/10 px-3 py-2 bg-white/5 focus:outline-none focus:ring-2 focus:ring-primary/30"
              rows={4}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              maxLength={6000}
            />
          </label>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger
              className="w-full"
              aria-label={fr ? "Portée de mémoire" : "Memory scope"}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="USER">
                {fr ? "Personnel" : "Personal"}
              </SelectItem>
              {canManage && !!projectId && (
                <SelectItem value="PROJECT">
                  {fr ? "Projet (partagé)" : "Project (shared)"}
                </SelectItem>
              )}
            </SelectContent>
          </Select>

          <Button
            disabled={!content.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            {fr ? "Enregistrer" : "Save"}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-foreground/70">
            {fr
              ? "Connectez votre agent à ce projet. Choisissez ses permissions : les modifications simples peuvent être directes ; les actions groupées ou destructives demandent une confirmation dans le client, ou sur le site si nécessaire."
              : "Connect your agent to this project. Choose its permissions: simple edits can run directly; bulk or destructive actions require confirmation in the client, or on the website when needed."}
          </p>
          <code className="text-xs break-all">
            {window.location.origin}/api/mcp
          </code>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant={!tokenHistory ? "default" : "outline"} onClick={() => setTokenHistory(false)}>{fr ? "Jetons actifs" : "Active tokens"}</Button>
            <Button size="sm" variant={tokenHistory ? "default" : "outline"} onClick={() => setTokenHistory(true)}>{fr ? "Historique des jetons" : "Token history"}</Button>
          </div>
          <p className="text-xs text-foreground-muted">{fr ? "Seuls les jetons couvrant ce projet sont affichés. Les secrets ne sont stockés que sous forme de hash : remplacez un jeton perdu, puis mettez à jour votre client." : "Only tokens covering this project are shown. Secrets are stored as hashes: replace a lost token, then update your client."}</p>
          {!tokens.isPending && !tokens.data?.tokens.length && <p className="text-sm text-foreground-muted">{fr ? "Aucun jeton dans cette liste." : "No tokens in this list."}</p>}
          {tokens.data?.tokens.map((token) => (
            <div
              key={token.id}
              className="flex flex-wrap gap-2 items-center rounded border border-white/10 p-2 text-sm"
            >
              <span className="min-w-0 basis-full sm:basis-auto sm:flex-1">
                {token.label} · {new Date(token.expiresAt).toLocaleDateString()}
              </span>
              {!tokenHistory ? (
                <>
                <Button variant="outline" size="sm" onClick={() => setReplaceId(token.id)}>{fr ? "Remplacer" : "Replace"}</Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => revoke.mutate(token.id)}
                >
                  {fr ? "Révoquer" : "Revoke"}
                </Button>
                </>
              ) : (
                <span>{token.revokedAt ? (fr ? "Révoqué" : "Revoked") : (fr ? "Expiré" : "Expired")}</span>
              )}
            </div>
          ))}
          {replaceId && <div className="rounded-lg border border-primary/30 p-3 space-y-2">
            <p className="text-sm">{fr ? "Le remplacement révoque immédiatement l’ancien jeton et conserve ses projets et permissions. Le nouveau expire dans 30 jours." : "Replacement immediately revokes the old token and preserves its projects and permissions. The new token expires in 30 days."}</p>
            <Button variant="ghost" size="sm" onClick={() => setReplaceId(null)}>{fr ? "Annuler" : "Cancel"}</Button>
            <Button size="sm" disabled={replace.isPending} onClick={() => replace.mutate(replaceId)}>{fr ? "Confirmer le remplacement" : "Confirm replacement"}</Button>
          </div>}
          <label className="block text-sm">
            {fr ? "Nom du client" : "Client name"}
            <input
              className="w-full mt-2 rounded-lg border border-white/10 px-3 py-2 bg-white/5 focus:outline-none focus:ring-2 focus:ring-primary/30"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </label>
          <div className="rounded-lg border border-white/10 p-3 text-sm space-y-2">
            <label className="flex gap-2">
              <Checkbox
                checked={tokenWrite}
                onCheckedChange={(value) => setTokenWrite(value === true)}
              />
              {fr
                ? "Autoriser les modifications simples immédiates"
                : "Allow immediate simple edits"}
            </label>
            <label className="flex gap-2">
              <Checkbox
                checked={tokenBulk}
                disabled={!tokenWrite}
                onCheckedChange={(value) => setTokenBulk(value === true)}
              />
              {fr
                ? "Autoriser les lots avec confirmation"
                : "Allow batches with confirmation"}
            </label>
            <label className="flex gap-2">
              <Checkbox
                checked={tokenDestructive}
                disabled={!tokenWrite}
                onCheckedChange={(value) => setTokenDestructive(value === true)}
              />
              {fr
                ? "Autoriser les suppressions avec confirmation"
                : "Allow destructive actions with confirmation"}
            </label>
            <p className="text-xs text-foreground/60">
              {fr
                ? "Les lots et suppressions montrent un aperçu et demandent confirmation dans votre client MCP. Si le client ne prend pas en charge cette confirmation, validez sur le site."
                : "Batches and destructive actions show a preview and request confirmation in your MCP client. If confirmation is unsupported, approve on the website."}
            </p>
          </div>
          <Button
            disabled={!label.trim() || createToken.isPending}
            onClick={() => createToken.mutate()}
          >
            {fr ? "Créer un jeton (30 jours)" : "Create a token (30 days)"}
          </Button>
          {newToken && (
            <div className="rounded border border-primary/30 p-3">
              <p className="text-sm mb-2">
                {fr
                  ? "Copiez ce jeton maintenant. Il ne sera plus affiché après fermeture."
                  : "Copy this token now. It will not be shown after closing."}
              </p>
              <input
                readOnly
                aria-label="MCP token"
                value={newToken}
                className="w-full bg-surface rounded border p-2 text-xs font-mono"
              />
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => navigator.clipboard.writeText(newToken)}
              >
                {fr ? "Copier" : "Copy"}
              </Button>
            </div>
          )}
        </div>
      )}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error.message}
        </p>
      )}
    </div>
  );
  if (embedded) return contents;
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
    >
      <DialogContent className="max-h-[85dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {fr ? "Mémoire et accès externes" : "Memory and external access"}
          </DialogTitle>
        </DialogHeader>
        {contents}
      </DialogContent>
    </Dialog>
  );
}
